import { fetchJSON as fetchAPI } from './client';

export type HierarchyNode = { key: string; name: string; level: number; parent_key?: string; description?: string };

export type SprintPhilosophyItem = { key: string; name: string; description: string; mandatory: boolean };

export type Epic = {
  key: string;
  name: string;
  capability: string;
  business_value: string;
  target_persona?: string;
  kpis?: string[];
  dependencies?: string[];
  sprint_target?: string;
  priority: string;
  status: string;
};

export type UserStoryTemplateField = { key: string; name: string; category: string; required: boolean; description?: string };

export type UserStory = {
  key: string;
  title: string;
  epic_key: string;
  business_goal: string;
  persona: string;
  acceptance_criteria?: string[];
  success_kpis?: string[];
  sprint?: string;
  story_points?: number;
  status: string;
};

export type TaskTemplateField = { key: string; name: string; category: string; required: boolean; description?: string };

export type EngineeringTask = {
  key: string;
  title: string;
  story_key: string;
  objective: string;
  complexity: string;
  dependencies?: string[];
  files?: string[];
  has_api: boolean;
  has_frontend: boolean;
  has_database: boolean;
  has_tests: boolean;
  status: string;
};

export type DependencyMatrixEntry = {
  key: string;
  source: string;
  depends_on: string[];
  sprint_dependencies?: string[];
  capability_dependencies?: string[];
  database_dependencies?: string[];
  api_dependencies?: string[];
  ai_dependencies?: string[];
  infrastructure_dependencies?: string[];
  critical_path: boolean;
};

export type SprintPlan = {
  key: string;
  name: string;
  number: number;
  goal: string;
  deliverables?: string[];
  epics?: string[];
  duration?: string;
  status: string;
};

export type SprintDefinitionOfDoneItem = { key: string; name: string; category: string; required: boolean };

export type BacklogDeliverable = { key: string; name: string; status: string; description?: string };

export type Milestone = { key: string; name: string; target_date?: string; sprint?: string; description?: string; status: string };

export type RiskRegisterEntry = { key: string; risk: string; impact: string; likelihood: string; mitigation: string; owner?: string; status: string };

export type ProductionChecklistItem = { key: string; item: string; category: string; required: boolean; status: string };

export type EBPDashboard = {
  bible_id: string;
  tenant_id: string;
  version: string;
  depends_on: string[];
  hierarchy_nodes: number;
  epics: number;
  user_stories: number;
  engineering_tasks: number;
  sprint_plans: number;
  dependency_entries: number;
  deliverables: number;
  milestones: number;
  risk_register: number;
  production_checklist: number;
  generated_at: string;
};

export type StoryValidation = {
  story_key: string;
  valid: boolean;
  business_goal_defined: boolean;
  persona_defined: boolean;
  acceptance_criteria: boolean;
  success_kpis: boolean;
  api_requirements: boolean;
  database_requirements: boolean;
  ui_components: boolean;
  security_requirements: boolean;
  test_cases: boolean;
  gaps?: string[];
  evaluated_at: string;
};

export type EBPBibleStatus = {
  bible_id: string;
  version: string;
  depends_on: string[];
  hierarchy_nodes: number;
  epics: number;
  user_stories: number;
  engineering_tasks: number;
  sprint_plans: number;
  dependency_entries: number;
  deliverables: number;
  milestones: number;
  risk_register: number;
  production_checklist: number;
  business_value_mapped: boolean;
  execution_ready: boolean;
  status: string;
};

const GOVERNANCE_BASE = import.meta.env.VITE_GOVERNANCE_API ?? '/api/v1/governance';

function fetchGovernance<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${GOVERNANCE_BASE}${path}`, init);
}

export const engineeringBacklogApi = {
  dashboard: () => fetchGovernance<EBPDashboard>('/ebp/dashboard'),
  hierarchy: () => fetchGovernance<{ hierarchy: HierarchyNode[] }>('/ebp/hierarchy').then((r) => r.hierarchy),
  sprintPhilosophy: () => fetchGovernance<{ philosophy: SprintPhilosophyItem[] }>('/ebp/sprint-philosophy').then((r) => r.philosophy),
  epics: () => fetchGovernance<{ epics: Epic[] }>('/ebp/epics').then((r) => r.epics),
  userStoryTemplate: () => fetchGovernance<{ template: UserStoryTemplateField[] }>('/ebp/user-story-template').then((r) => r.template),
  userStories: () => fetchGovernance<{ stories: UserStory[] }>('/ebp/user-stories').then((r) => r.stories),
  taskTemplate: () => fetchGovernance<{ template: TaskTemplateField[] }>('/ebp/task-template').then((r) => r.template),
  engineeringTasks: () => fetchGovernance<{ tasks: EngineeringTask[] }>('/ebp/engineering-tasks').then((r) => r.tasks),
  dependencyMatrix: () => fetchGovernance<{ dependencies: DependencyMatrixEntry[] }>('/ebp/dependency-matrix').then((r) => r.dependencies),
  sprintPlans: () => fetchGovernance<{ sprints: SprintPlan[] }>('/ebp/sprint-plans').then((r) => r.sprints),
  definitionOfDone: () => fetchGovernance<{ items: SprintDefinitionOfDoneItem[] }>('/ebp/definition-of-done').then((r) => r.items),
  deliverables: () => fetchGovernance<{ deliverables: BacklogDeliverable[] }>('/ebp/deliverables').then((r) => r.deliverables),
  milestones: () => fetchGovernance<{ milestones: Milestone[] }>('/ebp/milestones').then((r) => r.milestones),
  riskRegister: () => fetchGovernance<{ risks: RiskRegisterEntry[] }>('/ebp/risk-register').then((r) => r.risks),
  productionChecklist: () => fetchGovernance<{ checklist: ProductionChecklistItem[] }>('/ebp/production-checklist').then((r) => r.checklist),
  validateStory: (payload: { story_key: string; attrs: Record<string, boolean> }) =>
    fetchGovernance<StoryValidation>('/ebp/validate-story', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  status: () => fetchGovernance<EBPBibleStatus>('/ebp/status'),
};
